import { api } from '../../api/client'

/**
 * The most calls one Apply makes: 20 × the server's 500-row page is 10,000 rows. The server's own stop
 * (`applied_count` 0) ends a stuck run; this ends a run that never reaches it.
 */
export const MAX_APPLY_ROUNDS = 20

export type ApplyOutcome =
  | { kind: 'done'; applied: number; rounds: number }
  /** 404 (unknown, forgotten, learning off) or 409 (the category is gone; the server forgot the memory). */
  | { kind: 'gone'; reason: 'not_found' | 'category_unavailable'; applied: number; rounds: number }
  /** Any other non-2xx, or no response (`status` 0). */
  | { kind: 'failed'; status: number; applied: number; rounds: number }

/**
 * `POST /category-memory/{id}/apply` until the server says it is finished (backend guide §4): at most 500
 * rows a call, so call again while `remaining_count` > 0, and STOP when a call applied nothing — rows
 * another writer holds still count as remaining, and calling again would spin. Sequential by construction
 * (each call depends on the last answer), which is why this is not `runBulk` or `pool`.
 */
export async function applyCategoryMemory(memoryId: string): Promise<ApplyOutcome> {
  let applied = 0
  let rounds = 0
  while (rounds < MAX_APPLY_ROUNDS) {
    rounds++
    let res
    try {
      res = await api.POST('/api/v1/category-memory/{memory_id}/apply', { params: { path: { memory_id: memoryId } } })
    } catch {
      return { kind: 'failed', status: 0, applied, rounds }
    }
    const status = res.response.status
    if (status === 404) return { kind: 'gone', reason: 'not_found', applied, rounds }
    if (status === 409) return { kind: 'gone', reason: 'category_unavailable', applied, rounds }
    if (res.error !== undefined || res.data === undefined) return { kind: 'failed', status, applied, rounds }
    applied += res.data.applied_count
    if (res.data.remaining_count <= 0 || res.data.applied_count === 0) break
  }
  return { kind: 'done', applied, rounds }
}
